import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import Buttons from '../../../Table/Row/EditableWithButtons/Buttons';
import Search from './Search';
import InputWithButtons from '../InputWithButtons';

import formatBorrowerName from '../../../../helpers/formatBorrowerName';
import useInitialSearch from './hooks/useInitialSearch';
import saveSelectedResultAndRecordChange from './helpers/saveSelectedResultAndRecordChange';
import DocumentService from '../../../../services/DocumentService';
import useSetEntityById from './hooks/useSetEntityById';

export default function SearchableEntity({
  type,
  name,
  lenderId,
  document,
  refetch,
  value,
  onSubmit,
}) {
  const [isMessageVisible, setIsMessageVisible] = useState(true);
  const [newValue, setNewValue] = useState(value);

  // const borrowerName = 'Nitish Poddar';
  // const lenderId = 7;

  const { isFetching, borrower, vendor, isBorrower } = useInitialSearch({
    type,
    name,
    lenderId,
  });

  const isSet = useSetEntityById({
    setValue: setNewValue,
    document,
    type,
  });

  const hasBorrower = !!borrower;
  const hasVendor = !!vendor;

  const { mutate: updateDocument } = useMutation({
    // eslint-disable-next-line no-shadow
    mutationFn: ({ key, value }) => {
      const documentCopy = JSON.parse(JSON.stringify(document));

      return DocumentService.updateDocument({
        documentId: document.id,
        formValues: {
          ...documentCopy,
          parseur_result: { ...documentCopy.parseur_result, [key]: value },
        },
      });
    },
    onSuccess: () => {
      refetch();
    },
  });

  let message = '';

  if ((hasBorrower || hasVendor) && !isFetching && !isSet) {
    message = (
      <div className="flex items-center max-w-lg">
        <p>
          {isBorrower
            ? `A borrower by this name was found: ${formatBorrowerName(
                borrower
              )}. Does this note belong to this borrower?`
            : `A vendor by this name was found: ${vendor.name}. Is this the correct vendor?`}
        </p>
        <Buttons
          confirmLabel="Yes"
          cancelLabel="No"
          onCancel={() => {
            setIsMessageVisible(false);
          }}
          onSubmit={() => {
            saveSelectedResultAndRecordChange(
              isBorrower ? borrower : vendor,
              type,
              updateDocument
            );

            setIsMessageVisible(false);
          }}
        />
      </div>
    );
  } else if (isBorrower && !hasBorrower && !isFetching) {
    message = 'Note: This will be a new borrower.';
  } else if (!isBorrower && !hasVendor && !isFetching) {
    message =
      'Note: This vendor was not found and will need to be created or manually matched.';
  }

  if (isSet) {
    message = null;
  }

  return (
    <>
      <InputWithButtons
        value={newValue}
        setValue={setNewValue}
        formattedValue={newValue}
        canFieldBeEdited={!isSet}
        onSubmit={() => onSubmit({ value: newValue })}
      />

      <div className="ml-2 flex items-center gap-2">
        {isMessageVisible && <div className="max-w-xs">{message}</div>}
        {!isSet && (
          <div className="ml-4">
            <Search
              lenderId={lenderId}
              type={type}
              setIsMessageVisible={setIsMessageVisible}
              name={name}
              updateDocument={updateDocument}
            />
          </div>
        )}
      </div>
    </>
  );
}
